import {
  ChevronDown,
  Droplet,
  DropletOff,
  Mic,
  MicOff,
  MonitorPause,
  MonitorPlay,
  Video,
  VideoOff,
} from "lucide-react";
import { socket } from "../mediasoup/socket";
import type { StreamItem } from "../../types/meeting.types";
import { getAutoPosition } from "../../utils/getAutoPosition";
import { useEffect, useState } from "react";
import { useOverlayControls } from "../../hooks/useOverlayControls";
import FloatingPortal from "../common/FloatingPortal";
import MicSelector from "../common/MicSelector";
import { useMeetingStore } from "../../store/meeting.store";
import CameraSelector from "../common/CameraSelector";

const getStreamAudioDeviceId = (stream?: MediaStream) =>
  stream
    ? (stream as any).__audioDeviceId ||
      stream.getAudioTracks()[0]?.getSettings().deviceId ||
      ""
    : "";

const getStreamVideoDeviceId = (stream?: MediaStream) =>
  stream
    ? (stream as any).__videoDeviceId ||
      stream.getVideoTracks()[0]?.getSettings().deviceId ||
      ""
    : "";

export function toggleAudio(stream: StreamItem) {
  if (!stream.audioProducerId) return;

  socket.emit("toggle-media", {
    userId: stream.userId,
    producerId: stream.audioProducerId,
    kind: "audio",
    action: stream.audioPaused ? "resume" : "pause",
    message: `Your microphone has been ${stream.audioPaused ? "unmuted" : "muted"} by the Judge.`,
  });
}

export function toggleVideo(stream: StreamItem) {
  if (!stream.videoProducerId) return;

  socket.emit("toggle-media", {
    userId: stream.userId,
    producerId: stream.videoProducerId,
    kind: "video",
    action: stream.videoPaused ? "resume" : "pause",
    message: `The Judge has ${stream.videoPaused ? "enabled" : "disabled"} your camera.`,
  });
}

export function toggleFreeze(stream: StreamItem) {
  socket.emit("toggle-visual-effect", {
    userId: stream.userId,
    producerId: stream.producerId,
    effect: "freeze",
    enabled: !stream.frozen,
    message: `Your video has been ${stream.frozen ? "unfreezed" : "freezed"} by the Judge.`,
  });
}

export function toggleBlur(stream: StreamItem) {
  socket.emit("toggle-visual-effect", {
    userId: stream.userId,
    producerId: stream.producerId,
    effect: "blur",
    enabled: !stream.blurred,
    message: `Your video has been ${stream.blurred ? "unblurred" : "blurred"} by the Judge.`,
  });
}

interface RenderParticipantIconsProps {
  peer: StreamItem;
  activeDeviceSelector: string | null;
  setActiveDeviceSelector: (key: string | null) => void;
}

const RenderParticipantIcons = ({
  peer,
  activeDeviceSelector,
  setActiveDeviceSelector,
}: RenderParticipantIconsProps) => {
  // mic
  const [micUI, setMicUI] = useState<any>(null);
  const micAssignments = useMeetingStore((s) => s.micAssignments);
  const assignMic = useMeetingStore((s) => s.assignMic);

  // camera
  const cameraAssignments = useMeetingStore((s) => s.cameraAssignments);
  const assignCamera = useMeetingStore((s) => s.assignCamera);
  const [camUI, setCamUI] = useState<any>(null);
  const selectorOwner = peer.userId || peer.producerId || peer.name;
  const micSelectorKey = `${selectorOwner}:mic`;
  const cameraSelectorKey = `${selectorOwner}:camera`;
  const isMicSelectorOpen = activeDeviceSelector === micSelectorKey;
  const isCameraSelectorOpen = activeDeviceSelector === cameraSelectorKey;
  const selectedMicId =
    micAssignments[peer.userId] || getStreamAudioDeviceId(peer.stream);
  const selectedCameraId =
    cameraAssignments[peer.userId] || getStreamVideoDeviceId(peer.stream);

  useOverlayControls(() => {
    setMicUI(null);
    setCamUI(null);
    setActiveDeviceSelector(null);
  });

  useEffect(() => {
    if (activeDeviceSelector !== micSelectorKey) {
      setMicUI(null);
    }

    if (activeDeviceSelector !== cameraSelectorKey) {
      setCamUI(null);
    }
  }, [activeDeviceSelector, cameraSelectorKey, micSelectorKey]);

  return (
    <div className="absolute top-1 right-2 flex gap-2">
      {/* --------------------------- Mute ----------------------------- */}
      <div className="flex overflow-hidden rounded-full bg-blue-800">
        <button
          type="button"
          onClick={() => toggleAudio(peer)}
          title={peer.audioPaused ? "Unmute microphone" : "Mute microphone"}
          aria-label={peer.audioPaused ? "Unmute microphone" : "Mute microphone"}
          className="flex items-center justify-center p-2"
        >
          {peer.audioPaused ? (
            <MicOff size={16} className="text-white" />
          ) : (
            <Mic size={16} className="text-white" />
          )}
        </button>
        {peer.isLocal && (
          <button
            type="button"
            data-overlay-trigger
            title="Select microphone"
            aria-label="Select microphone"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              if (isMicSelectorOpen) {
                setMicUI(null);
                setActiveDeviceSelector(null);
                return;
              }

              setMicUI(getAutoPosition(rect, 360));
              setCamUI(null);
              setActiveDeviceSelector(micSelectorKey);
            }}
            className="border-l border-white/30 px-1.5 text-white"
          >
            <ChevronDown
              size={16}
              className={` ${isMicSelectorOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {/* --------------------------- Video ----------------------------- */}
      <div className="flex overflow-hidden rounded-full bg-blue-800">
        <button
          type="button"
          onClick={() => toggleVideo(peer)}
          title={peer.videoPaused ? "Turn camera on" : "Turn camera off"}
          aria-label={peer.videoPaused ? "Turn camera on" : "Turn camera off"}
          className="flex items-center justify-center p-2"
        >
          {peer.videoPaused ? (
            <VideoOff size={16} className="text-white" />
          ) : (
            <Video size={16} className="text-white" />
          )}
        </button>

        {peer.isLocal && (
          <button
            type="button"
            data-overlay-trigger
            title="Select camera"
            aria-label="Select camera"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              if (isCameraSelectorOpen) {
                setCamUI(null);
                setActiveDeviceSelector(null);
                return;
              }

              setCamUI(getAutoPosition(rect, 420));
              setMicUI(null);
              setActiveDeviceSelector(cameraSelectorKey);
            }}
            className="border-l border-white/30 px-1.5 text-white"
          >
            <ChevronDown
              size={16}
              className={`${isCameraSelectorOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {/* --------------------------- Blur ----------------------------- */}
      <button
        type="button"
        onClick={() => toggleBlur(peer)}
        title={peer.blurred ? "Remove blur" : "Blur video"}
        aria-label={peer.blurred ? "Remove blur" : "Blur video"}
        className="bg-blue-800 p-2 rounded-full"
      >
        {peer.blurred ? (
          <DropletOff size={16} className="text-white" />
        ) : (
          <Droplet size={16} className="text-white" />
        )}
      </button>

      {/* ---------------------------Freeze----------------------------- */}
      <button
        type="button"
        onClick={() => toggleFreeze(peer)}
        title={peer.frozen ? "Unfreeze video" : "Freeze video"}
        aria-label={peer.frozen ? "Unfreeze video" : "Freeze video"}
        className="bg-blue-800 p-2 rounded-full"
      >
        {peer.frozen ? (
          <MonitorPause size={16} className="text-white" />
        ) : (
          <MonitorPlay size={16} className="text-white" />
        )}
      </button>

      {/* MIC PICKER */}
      {micUI && isMicSelectorOpen && (
        <FloatingPortal>
          <div
            data-overlay
            className="animate-in fade-in zoom-in-95"
            style={{
              position: "fixed",
              top: micUI.top,
              left: micUI.left,
              zIndex: 999999,
            }}
          >
            <div
              className="absolute -top-2 w-3 h-3 bg-white rotate-45"
              style={{ left: micUI.arrowLeft }}
            />
            <MicSelector
              audioId={selectedMicId}
              onSelect={(mic) => {
                assignMic(peer.userId, mic.deviceId);
                setMicUI(null);
                setActiveDeviceSelector(null);
              }}
            />
          </div>
        </FloatingPortal>
      )}

      {/* CAMERA PICKER */}
      {camUI && isCameraSelectorOpen && (
        <FloatingPortal>
          <div
            data-overlay
            className="animate-in fade-in zoom-in-95"
            style={{
              position: "fixed",
              top: camUI.top,
              left: camUI.left,
              zIndex: 999999,
            }}
          >
            <div
              className="absolute -top-2 w-3 h-3 bg-white rotate-45"
              style={{ left: camUI.arrowLeft }}
            />
            <CameraSelector
              videoId={selectedCameraId}
              onSelect={(cam) => {
                assignCamera(peer.userId, cam.deviceId);
                setCamUI(null);
                setActiveDeviceSelector(null);
              }}
            />
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default RenderParticipantIcons;
